export type SortDirection = 'asc' | 'desc';
export type CandidateSortField =
  | 'name'
  | 'email'
  | 'years'
  | 'applied'
  | 'lastLogin'
  | 'matchScore';

export type TableCandidate = {
  id: number;
  email: string;
  first_name: string | null;
  last_name: string | null;
  applied_at: string | null;
  last_sign_in_at: string | null;
  years_of_experience: number | null;
};

export const getDisplayName = (candidate: Pick<TableCandidate, 'first_name' | 'last_name'>) =>
  [candidate.first_name, candidate.last_name].filter(Boolean).join(' ') || undefined;

export const compareNullable = <T>(
  left: T | null | undefined,
  right: T | null | undefined,
  compare: (a: T, b: T) => number,
  direction: SortDirection = 'asc'
): number => {
  if (left == null && right == null) {
    return 0;
  }
  if (left == null) {
    return 1;
  }
  if (right == null) {
    return -1;
  }
  const result = compare(left, right);
  return direction === 'asc' ? result : -result;
};

export const toSortableTimestamp = (value: string | null | undefined): number | null => {
  if (!value) {
    return null;
  }
  const parsed = Date.parse(value);
  return Number.isFinite(parsed) ? parsed : null;
};

export const sortCandidates = <T extends TableCandidate>(
  rows: T[],
  sort: { field: CandidateSortField; direction: SortDirection },
  matchScores: Record<number, number>
): T[] => {
  return [...rows].sort((left, right) => {
    let comparison = 0;

    switch (sort.field) {
      case 'name':
        comparison = (getDisplayName(left) ?? '').localeCompare(
          getDisplayName(right) ?? '',
          undefined,
          { sensitivity: 'base' }
        );
        break;
      case 'email':
        comparison = left.email.localeCompare(right.email, undefined, { sensitivity: 'base' });
        break;
      case 'years':
        comparison = compareNullable(
          left.years_of_experience,
          right.years_of_experience,
          (a, b) => a - b,
          sort.direction
        );
        break;
      case 'applied':
        comparison = compareNullable(
          toSortableTimestamp(left.applied_at),
          toSortableTimestamp(right.applied_at),
          (a, b) => a - b,
          sort.direction
        );
        break;
      case 'lastLogin':
        comparison = compareNullable(
          toSortableTimestamp(left.last_sign_in_at),
          toSortableTimestamp(right.last_sign_in_at),
          (a, b) => a - b,
          sort.direction
        );
        break;
      case 'matchScore':
        comparison = compareNullable(
          matchScores[left.id],
          matchScores[right.id],
          (a, b) => a - b,
          sort.direction
        );
        break;
      default:
        comparison = 0;
    }

    if (comparison === 0) {
      comparison = right.id - left.id;
    }

    if (sort.field === 'name' || sort.field === 'email') {
      return sort.direction === 'asc' ? comparison : -comparison;
    }

    return comparison;
  });
};

export const toggleSelectedId = (prev: number[], id: number, checked: boolean): number[] => {
  const exists = prev.includes(id);
  if (checked && !exists) {
    return [...prev, id];
  }
  if (!checked && exists) {
    return prev.filter(item => item !== id);
  }
  return prev;
};

export const toggleSelectedIds = (prev: number[], ids: number[], checked: boolean): number[] => {
  if (checked) {
    const next = new Set(prev);
    for (const id of ids) {
      next.add(id);
    }
    return [...next];
  }
  const remove = new Set(ids);
  return prev.filter(id => !remove.has(id));
};

export const escapeCsvField = (value: string): string => {
  const safe = /^[=+\-@]/.test(value) ? `'${value}` : value;
  if (/[",\n\r]/.test(safe)) {
    return `"${safe.replace(/"/g, '""')}"`;
  }
  return safe;
};
